/**
 * Astrology Module — powered by Swiss Ephemeris (WASM)
 *
 * Provides additional astrological data points beyond HD core calculations:
 * - Ascendant & MC (Medium Coeli)
 * - Chiron position
 * - Mean Black Moon Lilith
 * - Retrograde detection for all planets
 * - Zodiac sign mapping
 *
 * The core HD calculation (gates, lines, channels, type etc.) still uses
 * astronomy-engine for proven accuracy. This module adds supplementary data.
 */

let sweInstance = null;
let sweReady = false;
let sweInitPromise = null;

/**
 * Initialize Swiss Ephemeris WASM module (lazy, once).
 * The .data ephemeris file (12MB) is served from /swisseph.data (in public/).
 * The .wasm file is bundled by Vite as an asset.
 */
async function ensureSwe() {
  if (sweReady && sweInstance) return sweInstance;

  if (sweInitPromise) return sweInitPromise;

  sweInitPromise = (async () => {
    try {
      const { default: SwissEph } = await import('swisseph-wasm');
      sweInstance = new SwissEph();
      await sweInstance.initSwissEph();
      sweReady = true;
      console.log('Swiss Ephemeris WASM initialized');
      return sweInstance;
    } catch (err) {
      console.warn('Swiss Ephemeris WASM failed to load:', err);
      sweInitPromise = null;
      throw err;
    }
  })();

  return sweInitPromise;
}

// =================== Zodiac ===================

const ZODIAC_SIGNS = [
  { name: 'Widder',      symbol: '♈', element: 'Feuer',  modality: 'Kardinal' },
  { name: 'Stier',       symbol: '♉', element: 'Erde',   modality: 'Fix' },
  { name: 'Zwillinge',   symbol: '♊', element: 'Luft',   modality: 'Veränderlich' },
  { name: 'Krebs',       symbol: '♋', element: 'Wasser', modality: 'Kardinal' },
  { name: 'Löwe',        symbol: '♌', element: 'Feuer',  modality: 'Fix' },
  { name: 'Jungfrau',    symbol: '♍', element: 'Erde',   modality: 'Veränderlich' },
  { name: 'Waage',       symbol: '♎', element: 'Luft',   modality: 'Kardinal' },
  { name: 'Skorpion',    symbol: '♏', element: 'Wasser', modality: 'Fix' },
  { name: 'Schütze',     symbol: '♐', element: 'Feuer',  modality: 'Veränderlich' },
  { name: 'Steinbock',   symbol: '♑', element: 'Erde',   modality: 'Kardinal' },
  { name: 'Wassermann',  symbol: '♒', element: 'Luft',   modality: 'Fix' },
  { name: 'Fische',      symbol: '♓', element: 'Wasser', modality: 'Veränderlich' }
];

/**
 * Get zodiac sign info from ecliptic longitude
 */
export function getZodiacSign(longitude) {
  const normalized = ((longitude % 360) + 360) % 360;
  const idx = Math.floor(normalized / 30);
  const degree = normalized % 30;
  const sign = ZODIAC_SIGNS[idx];
  return {
    ...sign,
    degree: Math.floor(degree),
    minute: Math.floor((degree % 1) * 60),
    index: idx,
    formatted: `${sign.symbol} ${sign.name} ${Math.floor(degree)}°${Math.floor((degree % 1) * 60).toString().padStart(2, '0')}'`
  };
}

// =================== Chinese Zodiac ===================

const CHINESE_ANIMALS = [
  'Ratte', 'Büffel', 'Tiger', 'Hase', 'Drache', 'Schlange',
  'Pferd', 'Ziege', 'Affe', 'Hahn', 'Hund', 'Schwein'
];

const CHINESE_ELEMENTS_CYCLE = ['Holz', 'Feuer', 'Erde', 'Metall', 'Wasser'];

/**
 * Get Chinese zodiac animal + element from birth year
 * (Simplified — ignores lunar new year cutoff)
 */
export function getChineseZodiac(year) {
  const animalIdx = ((year - 4) % 12 + 12) % 12;
  const elementIdx = Math.floor(((year - 4) % 10) / 2);
  return {
    animal: CHINESE_ANIMALS[animalIdx],
    element: CHINESE_ELEMENTS_CYCLE[elementIdx],
    formatted: `${CHINESE_ELEMENTS_CYCLE[elementIdx]}-${CHINESE_ANIMALS[animalIdx]}`
  };
}

// =================== Swiss Ephemeris Calculations ===================

/**
 * Convert a JS Date (UTC) to Julian Day number
 */
function dateToJD(swe, utcDate) {
  return swe.julday(
    utcDate.getUTCFullYear(),
    utcDate.getUTCMonth() + 1,
    utcDate.getUTCDate(),
    utcDate.getUTCHours() + utcDate.getUTCMinutes() / 60 + utcDate.getUTCSeconds() / 3600
  );
}

/**
 * Calculate extended astrological data for a chart
 *
 * @param {Date} utcDate - Birth date/time in UTC
 * @param {number} lat - Geographic latitude
 * @param {number} lon - Geographic longitude
 * @returns {object} Extended astrology data
 */
export async function calculateAstrology(utcDate, lat, lon) {
  const swe = await ensureSwe();
  const jd = dateToJD(swe, utcDate);
  const flags = swe.SEFLG_SWIEPH | swe.SEFLG_SPEED;

  // --- Ascendant & MC (Placidus house system) ---
  let ascendant = null;
  let mc = null;
  let houses = null;

  if (lat !== undefined && lon !== undefined) {
    try {
      const houseResult = swe.houses(jd, lat, lon, 'P'); // Placidus
      ascendant = {
        longitude: houseResult.ascmc[0],
        zodiac: getZodiacSign(houseResult.ascmc[0])
      };
      mc = {
        longitude: houseResult.ascmc[1],
        zodiac: getZodiacSign(houseResult.ascmc[1])
      };
      houses = Array.from(houseResult.cusps).slice(1, 13).map((cusp, i) => ({
        house: i + 1,
        longitude: cusp,
        zodiac: getZodiacSign(cusp)
      }));
    } catch (err) {
      console.warn('House calculation failed:', err);
    }
  }

  // --- Chiron ---
  let chiron = null;
  try {
    const chironResult = swe.calc_ut(jd, swe.SE_CHIRON, flags);
    chiron = {
      longitude: chironResult[0],
      speed: chironResult[3],
      retrograde: chironResult[3] < 0,
      zodiac: getZodiacSign(chironResult[0])
    };
  } catch (err) {
    console.warn('Chiron calculation failed:', err);
  }

  // --- Mean Black Moon Lilith (SE_MEAN_APOG = 12) ---
  let lilith = null;
  try {
    const lilithResult = swe.calc_ut(jd, 12, flags);
    lilith = {
      longitude: lilithResult[0],
      speed: lilithResult[3],
      zodiac: getZodiacSign(lilithResult[0])
    };
  } catch (err) {
    console.warn('Lilith calculation failed:', err);
  }

  // --- Retrograde status for all planets ---
  const planetRetrogrades = {};
  const planetMap = [
    [swe.SE_MERCURY, 'Mercury'],
    [swe.SE_VENUS, 'Venus'],
    [swe.SE_MARS, 'Mars'],
    [swe.SE_JUPITER, 'Jupiter'],
    [swe.SE_SATURN, 'Saturn'],
    [swe.SE_URANUS, 'Uranus'],
    [swe.SE_NEPTUNE, 'Neptune'],
    [swe.SE_PLUTO, 'Pluto']
  ];

  for (const [planetId, planetName] of planetMap) {
    try {
      const result = swe.calc_ut(jd, planetId, flags);
      planetRetrogrades[planetName] = {
        speed: result[3],
        retrograde: result[3] < 0
      };
    } catch (err) {
      // skip
    }
  }

  // --- Zodiac signs for HD planets (using existing longitudes) ---
  // This will be merged with HD activations in chart.js

  return {
    ascendant,
    mc,
    houses,
    chiron,
    lilith,
    planetRetrogrades,
    julianDay: jd
  };
}

/**
 * Add zodiac + retrograde info to existing HD activations
 */
export function enrichActivations(activations, retrogrades) {
  return activations.map(act => ({
    ...act,
    zodiac: getZodiacSign(act.longitude),
    retrograde: retrogrades?.[act.planet]?.retrograde || false
  }));
}

/**
 * Check if Swiss Ephemeris is available
 */
export function isSweAvailable() {
  return sweReady;
}
